import { useEffect, useRef } from 'react';
import { Platform, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';

import { tokens } from '../../tokens';
import { useAnchorSectionList } from '../anchor-section-list/context';
import { Badge } from '../badge';
import { Pressable, PressableProps, usePressableState } from '../pressable';
import { Text, UiTextProps } from '../text';

const SCROLL_BUFFER = 48;

const webPointerPassthrough =
  Platform.OS === 'web' ? ({ pointerEvents: 'none' } as const) : {};

interface Props extends PressableProps {
  isActive: boolean;
  onPress: PressableProps['onPress'];
  title: string;
  titleStyle?: StyleProp<TextStyle>;
  scrollIntoFirstPositionInView?: (opts: { position: number; offset?: number }) => void;
  badge?: number;
  style?: StyleProp<ViewStyle>;
  titleTextProps?: Partial<UiTextProps>;
}

export const Anchor = (props: Props) => {
  const ref = useRef<View>(null);
  const layoutRef = useRef({ x: 0, y: 0, height: 0, width: 0 });
  const { manualActiveSectionId } = useAnchorSectionList();

  const {
    isActive,
    title,
    badge,
    scrollIntoFirstPositionInView,
    onPress,
    style,
    titleStyle,
    titleTextProps,
    accessibilityRole = 'button',
    ...pressableProps
  } = props;
  const wasActiveRef = useRef(isActive);

  useEffect(() => {
    if (!isActive) {
      ref.current?.blur?.();
      wasActiveRef.current = false;
      return;
    }

    const becameActive = !wasActiveRef.current;
    wasActiveRef.current = true;

    if (!becameActive || manualActiveSectionId || !scrollIntoFirstPositionInView) {
      return;
    }

    scrollIntoFirstPositionInView({
      position: layoutRef.current.x,
      offset: SCROLL_BUFFER,
    });
  }, [isActive, manualActiveSectionId, scrollIntoFirstPositionInView]);

  return (
    <Pressable
      ref={ref}
      {...pressableProps}
      onLayout={event => {
        layoutRef.current = event.nativeEvent.layout;
      }}
      borderRadius={0}
      style={[styles.pressable, isActive && styles.isActivePressable, style]}
      focusedStyle={styles.isFocusedPressable}
      hoveredStyle={styles.isHoveredPressable}
      outlineStyle={styles.hideOutline}
      outlinePressedStyle={styles.hideOutline}
      outlineFocusedStyle={styles.hideOutline}
      outlineDisabledStyle={styles.hideOutline}
      outlineHoveredStyle={styles.hideOutline}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={title}
      accessibilityState={{ selected: isActive }}
      onPress={event => {
        ref.current?.blur?.();
        onPress?.(event);
      }}
    >
      <Title isActive={isActive} title={title} style={titleStyle} titleTextProps={titleTextProps} />
      {badge && (
        <Badge
          circular
          circularSize={20}
          style={{ marginLeft: 4 }}
          color={tokens.colors.$houseRed}
          pointerEvents={Platform.OS === 'web' ? 'none' : undefined}
        >
          {badge}
        </Badge>
      )}
    </Pressable>
  );
};

const Title = ({
  isActive,
  title,
  style,
  titleTextProps,
}: {
  isActive: boolean;
  title: string;
  style?: StyleProp<TextStyle>;
  titleTextProps?: Partial<UiTextProps>;
}) => {
  const pressableState = usePressableState();
  return (
    <Text.Ui
      accessible={false}
      importantForAccessibility="no"
      size="sm"
      weight="semibold"
      style={[
        isActive && styles.isActiveText,
        pressableState.hovered && styles.isHoveredText,
        style,
      ]}
      {...webPointerPassthrough}
      {...titleTextProps}
    >
      {title}
    </Text.Ui>
  );
};

const styles = StyleSheet.create({
  pressable: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 0,
    borderBottomWidth: 3,
    borderColor: tokens.colors.$blackOpacity10,
    flexDirection: 'row',
  },
  isActivePressable: {
    borderColor: tokens.colors.$houseRed,
  },
  isHoveredPressable: {
    borderColor: tokens.colors.$houseRed,
  },
  isFocusedPressable: {
    borderColor: tokens.colors.$houseRed,
  },
  isActiveText: {
    color: tokens.colors.$houseRed,
  },
  isHoveredText: {
    color: tokens.colors.$houseRed,
  },
  isFocusedText: {
    color: tokens.colors.$houseRed,
  },
  hideOutline: {
    borderColor: tokens.colors.$transparent,
  },
});
